/**
 * Centralized State Logger for Space Management - FlexDisplay Original
 * Tracks all state changes from API calls and socket updates
 */

class StateLogger {
  constructor() {
    this.logs = [];
    this.maxLogs = 1000; // Keep last 1000 logs
    this.isEnabled = true;
    this.listeners = [];
    
    console.log('🔧 StateLogger initialized for FlexDisplay');
  }

  /**
   * Log API-based state updates
   */
  logApiUpdate(source, data, metadata = {}) {
    if (!this.isEnabled) return;

    const logEntry = {
      id: this.generateId(),
      timestamp: new Date().toISOString(),
      type: 'API',
      source,
      data: this.sanitizeData(data),
      metadata,
      spaceCount: Array.isArray(data) ? data.length : (data.spaceId ? 1 : 0)
    };

    this.addLog(logEntry);
    this.notifyListeners(logEntry);
  }

  /**
   * Log socket-based state updates
   */
  logSocketUpdate(source, data, metadata = {}) {
    if (!this.isEnabled) return;

    const logEntry = {
      id: this.generateId(),
      timestamp: new Date().toISOString(),
      type: 'SOCKET',
      source,
      data: this.sanitizeData(data),
      metadata,
      spaceId: data.spaceId || null,
      dataType: data.dataType || null
    };

    this.addLog(logEntry);
    this.notifyListeners(logEntry);
  }

  /**
   * Log space state changes
   */
  logStateChange(spaceId, spaceName, oldState, newState, source) {
    if (!this.isEnabled) return;

    const logEntry = {
      id: this.generateId(),
      timestamp: new Date().toISOString(),
      type: 'STATE_CHANGE',
      source,
      spaceId,
      spaceName,
      oldState: this.sanitizeData(oldState),
      newState: this.sanitizeData(newState),
      changes: this.getStateChanges(oldState, newState)
    };

    this.addLog(logEntry);
    this.notifyListeners(logEntry);
  }

  /**
   * Log connection status changes
   */
  logConnectionStatus(type, status, metadata = {}) {
    if (!this.isEnabled) return;

    const logEntry = {
      id: this.generateId(),
      timestamp: new Date().toISOString(),
      type: 'CONNECTION',
      source: type,
      status,
      metadata
    };

    this.addLog(logEntry);
    this.notifyListeners(logEntry);
  }

  /**
   * Get recent logs with filtering
   */
  getLogs(filter = {}) {
    let filteredLogs = [...this.logs];

    if (filter.type) {
      filteredLogs = filteredLogs.filter(log => log.type === filter.type);
    }

    if (filter.source) {
      filteredLogs = filteredLogs.filter(log => log.source === filter.source);
    }

    if (filter.spaceId) {
      filteredLogs = filteredLogs.filter(log => log.spaceId === filter.spaceId);
    }

    if (filter.limit) {
      filteredLogs = filteredLogs.slice(-filter.limit);
    }

    return filteredLogs.reverse(); // Most recent first
  }

  /**
   * Get state summary
   */
  getStateSummary() {
    const summary = {
      totalLogs: this.logs.length,
      apiUpdates: this.logs.filter(log => log.type === 'API').length,
      socketUpdates: this.logs.filter(log => log.type === 'SOCKET').length,
      stateChanges: this.logs.filter(log => log.type === 'STATE_CHANGE').length,
      connectionEvents: this.logs.filter(log => log.type === 'CONNECTION').length,
      lastUpdate: this.logs.length > 0 ? this.logs[this.logs.length - 1].timestamp : null,
      spacesAffected: new Set(this.logs.filter(log => log.spaceId).map(log => log.spaceId)).size
    };

    return summary;
  }

  /**
   * Print formatted summary to console
   */
  printSummary() {
    const summary = this.getStateSummary();
    console.group('📊 Space State Summary');
    console.log('Total Logs:', summary.totalLogs);
    console.log('API Updates:', summary.apiUpdates);
    console.log('Socket Updates:', summary.socketUpdates);
    console.log('State Changes:', summary.stateChanges);
    console.log('Connection Events:', summary.connectionEvents);
    console.log('Spaces Affected:', summary.spacesAffected);
    console.log('Last Update:', summary.lastUpdate);
    console.groupEnd();
    return summary;
  }

  /**
   * Print recent logs to console
   */
  printRecentLogs(limit = 10, type = null) {
    const filter = { limit };
    if (type) filter.type = type;
    
    const logs = this.getLogs(filter);
    console.group(`📋 Recent ${type || 'All'} Logs (${logs.length})`);
    
    logs.forEach(log => {
      const time = new Date(log.timestamp).toLocaleTimeString();
      console.log(`[${log.type}] ${log.source} - ${time}`, log.spaceId ? `Space: ${log.spaceId}` : '');
      
      if (log.changes && Object.keys(log.changes).length > 0) {
        Object.entries(log.changes).forEach(([sensor, change]) => {
          console.log(`  ${sensor}: ${this.getStateValue(change.from)} → ${this.getStateValue(change.to)}`);
        });
      }
    });
    
    console.groupEnd();
    return logs;
  }

  /**
   * Monitor specific space
   */
  monitorSpace(spaceId) {
    console.log(`🔍 Monitoring space ${spaceId}...`);
    
    const listener = (logEntry) => {
      if (logEntry.spaceId === spaceId) {
        const time = new Date(logEntry.timestamp).toLocaleTimeString();
        console.log(`🔄 [${logEntry.type}] ${logEntry.source} - ${time}`);
        
        if (logEntry.changes) {
          Object.entries(logEntry.changes).forEach(([sensor, change]) => {
            console.log(`   ${sensor}: ${this.getStateValue(change.from)} → ${this.getStateValue(change.to)}`);
          });
        }
      }
    };

    this.addListener(listener);
    
    // Return function to stop monitoring
    return () => {
      this.removeListener(listener);
      console.log(`⏹️ Stopped monitoring space ${spaceId}`);
    };
  }

  /**
   * Clear all logs
   */
  clearLogs() {
    this.logs = [];
    console.log('🗑️ StateLogger: All logs cleared');
  }

  /**
   * Enable/disable logging
   */
  setEnabled(enabled) {
    this.isEnabled = enabled;
    console.log(`StateLogger: ${enabled ? '✅ Enabled' : '❌ Disabled'}`);
  }

  /**
   * Add listener for real-time log updates
   */
  addListener(callback) {
    this.listeners.push(callback);
  }

  /**
   * Remove listener
   */
  removeListener(callback) {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }

  // Private methods
  addLog(logEntry) {
    this.logs.push(logEntry);
    
    // Keep only recent logs
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Console output for debugging
    this.consoleOutput(logEntry);
  }

  consoleOutput(logEntry) {
    const style = this.getConsoleStyle(logEntry.type);
    const time = new Date(logEntry.timestamp).toLocaleTimeString();
    
    //console.groupCollapsed(`%c[${logEntry.type}] ${logEntry.source} - ${time}`, style);
    
    if (logEntry.spaceId) {
      console.log('🏢 Space ID:', logEntry.spaceId);
    }
    
    if (logEntry.spaceName) {
      console.log('📝 Space Name:', logEntry.spaceName);
    }
    
    if (logEntry.dataType) {
      console.log('📊 Data Type:', logEntry.dataType);
    }
    
    if (logEntry.status) {
      console.log('🔌 Status:', logEntry.status);
    }
    
    if (logEntry.changes && Object.keys(logEntry.changes).length > 0) {
      console.log('🔄 Changes:');
      Object.entries(logEntry.changes).forEach(([sensor, change]) => {
        console.log(`  ${sensor}: ${this.getStateValue(change.from)} → ${this.getStateValue(change.to)}`);
      });
    }
    
    if (logEntry.spaceCount) {
      console.log('📊 Spaces Affected:', logEntry.spaceCount);
    }
    
    if (logEntry.metadata && Object.keys(logEntry.metadata).length > 0) {
      console.log('ℹ️ Metadata:', logEntry.metadata);
    }
    
    console.groupEnd();
  }

  getConsoleStyle(type) {
    const styles = {
      'API': 'color: #2196F3; font-weight: bold; background: #e3f2fd; padding: 2px 6px; border-radius: 3px;',
      'SOCKET': 'color: #4CAF50; font-weight: bold; background: #e8f5e8; padding: 2px 6px; border-radius: 3px;',
      'STATE_CHANGE': 'color: #FF9800; font-weight: bold; background: #fff3e0; padding: 2px 6px; border-radius: 3px;',
      'CONNECTION': 'color: #9C27B0; font-weight: bold; background: #f3e5f5; padding: 2px 6px; border-radius: 3px;'
    };
    return styles[type] || 'color: #666;';
  }

  generateId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  sanitizeData(data) {
    if (!data) return null;
    
    // Remove circular references and limit depth
    try {
      return JSON.parse(JSON.stringify(data));
    } catch (e) {
      return { error: 'Could not serialize data', type: typeof data };
    }
  }

  getStateChanges(oldState, newState) {
    const changes = {};
    
    if (!oldState || !newState) return changes;
    
    // Compare availability states
    if (oldState.availability?.state !== newState.availability?.state) {
      changes.availability = {
        from: oldState.availability?.state,
        to: newState.availability?.state
      };
    }
    
    // Compare sensor states
    ['spot', 'device', 'flo', 'meta'].forEach(sensor => {
      if (oldState[sensor]?.state !== newState[sensor]?.state) {
        changes[sensor] = {
          from: oldState[sensor]?.state,
          to: newState[sensor]?.state
        };
      }
    });
    
    return changes;
  }

  getStateValue(state) {
    const stateNames = {
      0: 'ABSENCE',
      1: 'PRESENCE', 
      2: 'AVAILABLE',
      3: 'ALLOCATED',
      4: 'CHECKIN',
      5: 'CHECKOUT',
      '-1': 'UNKNOWN'
    };
    return stateNames[state] || state;
  }

  notifyListeners(logEntry) {
    this.listeners.forEach(callback => {
      try {
        callback(logEntry);
      } catch (e) {
        console.error('StateLogger: Error in listener callback', e);
      }
    });
  }
}

// Create singleton instance
const stateLogger = new StateLogger();

// Global access for debugging
if (typeof window !== 'undefined') {
  window.stateLogger = stateLogger;
  
  // Add convenient global methods
  window.spaceDebug = {
    summary: () => stateLogger.printSummary(),
    logs: (limit, type) => stateLogger.printRecentLogs(limit, type),
    monitor: (spaceId) => stateLogger.monitorSpace(spaceId),
    clear: () => stateLogger.clearLogs(),
    enable: (enabled) => stateLogger.setEnabled(enabled)
  };
  
  console.log('🔧 Space State Logger available at: window.stateLogger');
  console.log('🔧 Quick debug commands at: window.spaceDebug');
  console.log('   spaceDebug.summary() - Show overview');
  console.log('   spaceDebug.logs(10) - Show recent logs');
  console.log('   spaceDebug.monitor(12345) - Monitor space');
}

export default stateLogger;
